import React, { useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { Eye, Pencil, Search, Trash } from "react-bootstrap-icons";
import BootstrapTable from "react-bootstrap-table-next";
import filterFactory from "react-bootstrap-table2-filter";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { useNavigate } from "react-router-dom";
import {
  useRemoveAdminTemplatesMutation,
  useRemoveTemplateMutation,
  useTemplateListQuery,
} from "../../graphql/template.hooks";
import { useUserQuery } from "../../graphql/menu.hooks";
import DeletePageFolderModal from "../../components/modals/deletePageFolderModal";

export const Templates = () => {
  const settings = JSON.parse(localStorage.getItem("mePageSettings"));
  const navigate = useNavigate();
  const { data: user, loading: userLoading } = useUserQuery();
  const {
    data: templateList,
    loading: listLoading,
    refetch: refetchTemplates,
  } = useTemplateListQuery({
    variables: { userId: user?.user?.id },
    nextFetchPolicy: "network-only",
  });
  const [deleteTemplate] = useRemoveTemplateMutation();
  const [deleteTemplates, { loading: templatesLoading }] =
    useRemoveAdminTemplatesMutation();

  const [search, setSearch] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showConfirmModalDeleteAll, setShowConfirmModalDeleteAll] =
    useState<boolean>(false);
  const [activeTemplate, setActiveTemplate] = useState<number | null>(null);
  const [filteredTemplates, setFilteredTemplates] = useState(
    templateList?.adminTemplateList || []
  );
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    settings?.templateSettings?.currentPage || 1
  );
  const [limit, setLimit] = useState(settings?.templateSettings?.limit || 3);

  useEffect(() => {
    document.title = "Templates";
  }, []);

  const moveToEditForm = (templateId: number) => {
    navigate(`/template/${templateId}/edit`);
  };

  const moveToShowTemplate = (templateId: number) => {
    navigate(`/template/${templateId}`);
  };

  const numericSort = (a: any, b: any, order: any) => {
    if (order === "asc") {
      return a - b;
    }
    return b - a;
  };

  const textSort = (a: any, b: any, order: any) => {
    if (order === "asc") {
      return a.localeCompare(b);
    }
    return b.localeCompare(a);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearch(query);

    if (query === "") {
      setFilteredTemplates(templateList?.adminTemplateList);
      return;
    }

    const filtered = templateList?.adminTemplateList
      .filter((template) => {
        if (template) {
          const regex = new RegExp(query, "i");
          return regex.test(template?.name) || regex.test(String(template?.id));
        } else {
          return false;
        }
      })
      .map((item, index) => ({ ...item, key: index }));
    setCurrentPage(1);
    setFilteredTemplates(filtered);
  };

  const tryDeleteTemplate = async (templateId: number) => {
    if (templateId === null) return;
    try {
      const { data } = await deleteTemplate({ variables: { id: +templateId } });
      if (data.adminRemoveTemplate) {
        refetchTemplates();
        backToList();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const tryDeleteAllTemplates = async () => {
    try {
      const { data } = await deleteTemplates({
        variables: { ids: selectedTemplates },
      });

      if (data.removeAdminTemplates) {
        setSelectedTemplates([]);
        refetchTemplates();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeCheckbox = (templateId: number, checked: boolean) => {
    setSelectedTemplates((prevSelectedTemplates) => {
      const updatedTemplates = checked
        ? [...prevSelectedTemplates, templateId]
        : prevSelectedTemplates.filter((id) => templateId !== id);

      return updatedTemplates;
    });
  };

  const backToList = () => {
    navigate("/template");
  };

  const columns = [
    {
      dataField: "id",
      text: "ID",
      sort: true,
      sortFunc: numericSort,
      style: {
        width: "75px",
      },
      formatter: (cell: string) => {
        return (
          <div className="d-flex flex-column align-items-center">
            <input
              id={`check-${cell}`}
              className="template-checkbox form-check-input m-0 mb-1"
              type="checkbox"
              checked={selectedTemplates.includes(+cell)}
              onChange={(e) => handleChangeCheckbox(+cell, e.target.checked)}
            />
            <label htmlFor={`check-${cell}`} className="form-check-label">
              {cell}
            </label>
          </div>
        );
      },
    },
    {
      dataField: "img",
      text: "Preview",
      formatter: (cell: any) => (
        <img
          src={cell?.length < 200 ? `https://cdn2.me-qr.com/${cell}` : cell}
          alt="Preview"
          style={{ width: "109px" }}
        />
      ),
      style: {
        width: "109px",
      },
    },
    {
      dataField: "name",
      text: "Name",
      sort: true,
      sortFunc: textSort,
    },
    {
      dataField: "content",
      text: "Content",
      sort: true,
      style: {
        maxWidth: "192px",
        minWidth: "192px",
      },
      formatter: (cell: any) => (
        <p className="text-break">{cell.slice(0, 250)}...</p>
      ),
      sortFunc: textSort,
    },
    {
      dataField: "isCommon",
      text: "Common",
      style: {
        width: "90px",
      },
      formatter: (cell: any) => (
        <p className={`${cell ? "text-success" : "text-danger"}`}>
          {String(cell)}
        </p>
      ),
    },
    {
      dataField: "users",
      text: "Users",
      formatter: (cell: any) => (
        <div>
          {cell?.map((user: any, index: any) => (
            <span
              key={index}
              className="bg-black fs-7 text-white rounded-5 d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }}
            >
              {user}
            </span>
          ))}
        </div>
      ),
    },
    {
      dataField: "actions",
      text: "Actions",
      style: {
        width: "108px",
      },
      formatter: (cell: any, row: any) => (
        <div className="d-flex">
          <button
            onClick={() => {
              setActiveTemplate(+row?.id);
              setShowConfirmModal(true);
            }}
            className="btn icon-hover btn-outline-danger danger-hover d-flex align-items-center justify-content-center p-0 me-2"
            style={{ width: "42px", height: "42px" }}
          >
            <Trash size={16} color="red" />
          </button>
          <button
            onClick={() => moveToEditForm(row?.id)}
            className="btn icon-hover btn-outline-primary primary-hover d-flex align-items-center justify-content-center p-0 me-2"
            style={{ width: "42px", height: "42px" }}
          >
            <Pencil size={16} color="#3A83F6" />
          </button>
          <button
            onClick={() => moveToShowTemplate(row?.id)}
            className="btn icon-hover btn-outline-primary primary-hover d-flex align-items-center justify-content-center p-0"
            style={{ width: "42px", height: "42px" }}
          >
            <Eye size={16} color="#3A83F6" />
          </button>
        </div>
      ),
    },
  ];

  const changeLocalStorageSettings = (
    type: "page" | "limit",
    value: number
  ) => {
    const updatedSettings =
      type === "page"
        ? { ...settings.templateSettings, currentPage: value }
        : { ...settings.templateSettings, limit: value };

    localStorage.setItem(
      "mePageSettings",
      JSON.stringify({
        ...settings,
        templateSettings: updatedSettings,
      })
    );
  };

  const paginationOptions = {
    custom: true,
    totalSize: search
      ? filteredTemplates.length
      : templateList?.adminTemplateList.length,
    sizePerPage: limit,
    page: currentPage,
    onPageChange: (page: number) => {
      changeLocalStorageSettings("page", page);
      setCurrentPage(page);
    },
    onSizePerPageChange: (size: number) => {
      changeLocalStorageSettings("limit", size);
      setLimit(size);
    },
    showTotal: true,
    sizePerPageList: [
      { text: "3 / rows on page", value: 3 },
      { text: "5 / rows on page", value: 5 },
      { text: "10 / rows on page", value: 10 },
      { text: "25 / rows on page", value: 25 },
    ],
  };

  const moveToCreateForm = () => {
    navigate("/template/new");
  };

  if (listLoading || userLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="templatesContainer container">
      <div className="d-flex justify-content-between align-items-center mb-3 mb-lg-4">
        <h1>Templates</h1>
        <div className="d-flex align-items-center col-6 col-lg-auto justify-content-end">
          <div className="col-12 col-lg templateSearch d-flex align-items-center px-2 pb-2 me-0 me-lg-4">
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by Title"
              className="templateInput text-gray me-3"
            />
            <Search size={16} color="#3E4957" />
          </div>
          <Button
            onClick={moveToCreateForm}
            className="d-none d-lg-block btn btn-primary text-white me-2"
          >
            Create New Template
          </Button>
          <Button
            disabled={!selectedTemplates.length || templatesLoading}
            onClick={() => setShowConfirmModalDeleteAll(true)}
            className="d-none d-lg-block btn btn-danger text-white px-3"
          >
            Delete All
            {templatesLoading && (
              <Spinner className="ms-2" animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </Button>
        </div>
      </div>
      <div className="col-12 pe-2 mb-4 d-flex d-lg-none">
        {" "}
        <Button
          onClick={moveToCreateForm}
          className="btn btn-primary col-6 me-2 text-white"
        >
          Create New
        </Button>
        <Button
          disabled={!selectedTemplates.length || templatesLoading}
          onClick={() => setShowConfirmModalDeleteAll(true)}
          className="btn btn-danger col-6 text-white px-3"
        >
          Delete All
          {templatesLoading && (
            <Spinner className="ms-2" animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          )}
        </Button>
      </div>
      <div className="g-4 py-4 px-3 d-flex flex-column justify-content-center align-items-center bg-white overflow-x-scroll rounded">
        <PaginationProvider pagination={paginationFactory(paginationOptions)}>
          {({ paginationProps, paginationTableProps }) => (
            <div className="col-12">
              <BootstrapTable
                classes="templatesTable"
                bootstrap4
                keyField="id"
                key={selectedTemplates.length}
                data={
                  search ? filteredTemplates : templateList?.adminTemplateList
                }
                columns={columns}
                filter={filterFactory()}
                bordered={false}
                striped
                hover
                remote={false}
                {...paginationTableProps}
              />
              <div className="d-flex justify-content-between align-items-center templatesPagination">
                <SizePerPageDropdownStandalone {...paginationProps} />
                <PaginationListStandalone {...paginationProps} />

                <span>
                  {(paginationProps.page - 1) * paginationProps.sizePerPage + 1}{" "}
                  -{" "}
                  {Math.min(
                    paginationProps.page * paginationProps.sizePerPage,
                    filteredTemplates.length
                  )}{" "}
                  of {filteredTemplates.length}
                </span>
              </div>
            </div>
          )}
        </PaginationProvider>
      </div>
      <DeletePageFolderModal
        title={"Delete Template"}
        description={"Are you sure you want to delete this template?"}
        showDeleteModal={showConfirmModal}
        handleCloseModal={() => setShowConfirmModal(false)}
        handleConfirmDelete={() => tryDeleteTemplate(activeTemplate)}
      />
      <DeletePageFolderModal
        title={"Delete Templates"}
        description={"Are you sure you want to delete all of templates?"}
        showDeleteModal={showConfirmModalDeleteAll}
        handleCloseModal={() => setShowConfirmModalDeleteAll(false)}
        handleConfirmDelete={tryDeleteAllTemplates}
      />
    </div>
  );
};
